/**
 * B2B portal — reorder submission.
 *
 * Adds the buyer's selected lines to the cart through the store's OWN cart
 * endpoint (routes.cart_add_url + ".js"). It does not import, wrap, patch or
 * re-implement any cart module: the global cart, the cart drawer, the product
 * form and the Pack Units system are all untouched by this file.
 *
 * ⚠️ PACK UNITS. The existing cart may still adjust quantities after items are
 * added. This file submits the requested quantities and nothing more; whatever
 * the cart does next is the cart's existing behaviour. The buyer is told this
 * in the markup. Nothing here bypasses or fixes it.
 *
 * ONE ITEM PER REQUEST, SEQUENTIALLY. A batch add fails atomically, which
 * would tell a buyer with ten lines only that "something" failed. Adding one
 * at a time reports exactly which line failed and why. Sequential rather than
 * parallel because concurrent writes to one cart race each other.
 *
 * Prices are never sent. The request carries a variant id and a quantity;
 * Shopify resolves the contextual price. There is no price in this file.
 *
 * Theme Editor safe: guarded against double-binding, listeners bound to the
 * component root and removed on disconnect, and every instance is independent
 * so two expanded orders never share state. No globals.
 */
(function () {
  "use strict";

  if (customElements.get("b2b-reorder-review")) return;

  class B2BReorderReview extends HTMLElement {
    connectedCallback() {
      if (this.dataset.b2bReady === "true") return;
      this.dataset.b2bReady = "true";

      this.button = this.querySelector("[data-b2b-reorder-submit]");
      this.status = this.querySelector("[data-b2b-reorder-status]");
      this.cartLink = this.querySelector("[data-b2b-reorder-cart]");
      if (!this.button) return;

      this.busy = false;
      this.onSubmit = this.handleSubmit.bind(this);
      this.button.addEventListener("click", this.onSubmit);
    }

    disconnectedCallback() {
      if (this.button && this.onSubmit) {
        this.button.removeEventListener("click", this.onSubmit);
      }
      this.dataset.b2bReady = "false";
    }

    msg(name) {
      return this.dataset["msg" + name] || "";
    }

    setStatus(text) {
      if (!this.status) return;
      this.status.textContent = text;
    }

    /**
     * Collect the checked lines. Quantity comes from the input, which carries
     * the CURRENT min/step/max, so the browser has already validated it
     * against today's rules rather than the rules that applied when the
     * original order was placed.
     */
    collect() {
      var rows = this.querySelectorAll("[data-b2b-reorder-check]");
      var items = [];
      var invalid = false;

      Array.prototype.forEach.call(rows, function (check) {
        if (!check.checked) return;

        var line = check.closest(".b2b-reline");
        var qtyInput = line ? line.querySelector("[data-b2b-reorder-qty]") : null;
        var id = check.getAttribute("data-b2b-variant-id");
        if (!id || !qtyInput) return;

        if (!qtyInput.checkValidity()) {
          invalid = true;
          qtyInput.reportValidity();
          return;
        }

        var quantity = parseInt(qtyInput.value, 10);
        if (!quantity || quantity < 1) {
          invalid = true;
          return;
        }

        items.push({
          id: id,
          quantity: quantity,
          result: line.querySelector("[data-b2b-reorder-result]"),
        });
      });

      return { items: items, invalid: invalid };
    }

    async handleSubmit() {
      if (this.busy) return;

      var collected = this.collect();

      if (collected.invalid) {
        this.setStatus(this.msg("Invalid"));
        return;
      }
      if (!collected.items.length) {
        this.setStatus(this.msg("None"));
        return;
      }

      this.busy = true;
      this.button.disabled = true;
      this.button.setAttribute("aria-busy", "true");
      this.setStatus(this.msg("Working"));

      var added = 0;
      var failed = 0;

      for (var i = 0; i < collected.items.length; i += 1) {
        var item = collected.items[i];
        var outcome = await this.addOne(item);

        if (item.result) {
          item.result.textContent = outcome.ok
            ? this.msg("LineAdded")
            : this.msg("LineFailed") + (outcome.reason ? " " + outcome.reason : "");
        }

        if (outcome.ok) added += 1;
        else failed += 1;
      }

      this.report(added, failed);

      this.busy = false;
      this.button.disabled = false;
      this.button.removeAttribute("aria-busy");
    }

    async addOne(item) {
      var base = this.dataset.b2bAddUrl || "/cart/add";
      var url = base.indexOf(".js") === -1 ? base + ".js" : base;

      try {
        var response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [{ id: item.id, quantity: item.quantity }],
          }),
        });

        if (response.ok) return { ok: true };

        // Shopify returns a human-readable description on 422; surface it
        // rather than a generic failure, because "minimum is 6" is actionable
        // and "could not add" is not.
        var reason = "";
        try {
          var payload = await response.json();
          reason = payload.description || payload.message || "";
        } catch (parseError) {
          reason = "";
        }
        return { ok: false, reason: reason };
      } catch (networkError) {
        return { ok: false, reason: "" };
      }
    }

    report(added, failed) {
      var text;

      if (failed === 0) text = this.msg("All").replace("{{ count }}", String(added));
      else if (added === 0) text = this.msg("Failed");
      else {
        text = this.msg("Partial")
          .replace("{{ added }}", String(added))
          .replace("{{ failed }}", String(failed));
      }

      this.setStatus(text);
      if (this.status) this.status.focus();

      if (added > 0 && this.cartLink) this.cartLink.hidden = false;
    }
  }

  customElements.define("b2b-reorder-review", B2BReorderReview);
})();
